import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { CHUNK_SIZE } from "./constants";

export function useChunkedUpload(axiosConfig) {
  const [state, setState] = useState({
    uploading: false, progress: 0, chunksDone: 0, chunksTotal: 0,
    fileName: "", error: null, uploadId: null, aborted: false
  });
  const abortRef = useRef(false);

  const reset = useCallback(() => {
    abortRef.current = false;
    setState({
      uploading: false, progress: 0, chunksDone: 0, chunksTotal: 0,
      fileName: "", error: null, uploadId: null, aborted: false
    });
  }, []);

  const abort = useCallback(async (uploadId) => {
    abortRef.current = true;
    setState((s) => ({ ...s, aborted: true, uploading: false }));
    if (uploadId) {
      try {
        await axios.delete(`/api/file-stream/upload/${uploadId}`, axiosConfig);
      } catch {}
    }
  }, [axiosConfig]);

  const upload = useCallback(async (file) => {
    if (!file) return null;
    abortRef.current = false;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    setState({
      uploading: true, progress: 0, chunksDone: 0, chunksTotal: totalChunks,
      fileName: file.name, error: null, uploadId: null, aborted: false
    });

    try {
      const initRes = await axios.post(
        "/api/file-stream/upload/init",
        { originalName: file.name, totalSize: file.size, totalChunks },
        axiosConfig
      );
      const { uploadId } = initRes.data;
      setState((s) => ({ ...s, uploadId }));

      let completedVideo = null;
      for (let i = 0; i < totalChunks; i++) {
        if (abortRef.current) throw new Error("Upload aborted by user");

        const start     = i * CHUNK_SIZE;
        const chunk     = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        const formData  = new FormData();
        formData.append("uploadId",    uploadId);
        formData.append("chunkIndex",  String(i));
        formData.append("chunk",       chunk, `chunk-${i}`);

        const chunkRes = await axios.post(
          "/api/file-stream/upload/chunk",
          formData,
          {
            ...axiosConfig,
            onUploadProgress: (e) => {
              if (e.total) {
                const overall = ((i + e.loaded / e.total) / totalChunks) * 100;
                setState((s) => ({ ...s, progress: Math.round(overall) }));
              }
            }
          }
        );
        setState((s) => ({ ...s, chunksDone: i + 1 }));
        if (chunkRes.data?.done && chunkRes.data?.video) {
          completedVideo = chunkRes.data.video;
        }
      }

      setState((s) => ({ ...s, uploading: false, progress: 100 }));
      return completedVideo;
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Upload failed";
      setState((s) => ({ ...s, uploading: false, error: msg, progress: 0 }));
      return null;
    }
  }, [axiosConfig]);

  return { state, upload, abort, reset };
}
